import { useEffect, useMemo, useRef, useState } from 'react';
import { getConversation, getLeadById, getLeadFields, listLeads, patchLead, saveLeadDetails, sendMessage, setAiPaused } from '../api';
import type { LeadField } from '../api';
import type { ConversationTurn, Lead, LeadStatus, Session } from '../types';

const STATUSES: LeadStatus[] = [
  'new', 'in-conversation', 'qualified', 'booked', 'estimate-sent', 'closed-won', 'closed-lost', 'needs-human',
];

function sourceLabel(s: string) {
  if (s === 'missed-call') return 'Missed call';
  if (s === 'inbound-sms' || s === 'sms') return 'Text';
  if (s.includes('website')) return 'Website';
  return s;
}

function sourceClass(s: string) {
  if (s === 'missed-call') return 'call';
  return 'sms';
}

function isToday(iso: string) {
  const d = new Date(iso);
  const n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
}

function when(iso: string) {
  return new Date(iso).toLocaleString('en-US', {
    weekday: 'short', month: 'numeric', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}
const STATUS_LABEL: Record<LeadStatus, string> = {
  new: 'New', 'in-conversation': 'In conversation', qualified: 'Qualified',
  booked: 'Estimate booked', 'estimate-sent': 'Estimate sent',
  'closed-won': 'Project closed', 'closed-lost': 'Lost', 'needs-human': 'Needs you',
  unresponsive: 'No reply',
};
function money(n?: number) {
  if (!n) return '—';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

type OptOut = { smsOptOut?: boolean; smsOptOutAt?: string };
const optedOut = (l: Lead) => (l as Lead & OptOut).smsOptOut === true;
const OPTOUT_TAG = { marginLeft: 8, fontSize: 11, padding: '1px 6px', borderRadius: 999, background: '#f6dcd6', color: '#8a1c1c', fontWeight: 500 } as const;

type LeadX = Lead & { smsOptOut?: boolean; aiPaused?: boolean; aiPausedReason?: 'owner' | 'handoff' };
const PAUSED_TAG = { marginLeft: 8, fontSize: 11, padding: '1px 6px', borderRadius: 999, background: '#e4e7ee', color: '#2f4a6b', fontWeight: 500 } as const;

function Composer({ session, lead, onChange }: {
  session: Session;
  lead: Lead;
  onChange: (patch: Partial<LeadX>) => Promise<void>;
}) {
  const l = lead as LeadX;
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { setText(''); setErr(''); }, [lead.leadId]);

  if (l.smsOptOut) return null; // the red opted-out note already explains why

  const n = text.trim().length;
  const segs = n <= 160 ? 1 : Math.ceil(n / 153);

  const send = async () => {
    if (!n || busy) return;
    setBusy(true);
    setErr('');
    try {
      await sendMessage(session, lead.leadId, text.trim());
      setText('');
      await onChange({ aiPaused: true, aiPausedReason: 'owner' });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Text not sent');
    } finally {
      setBusy(false);
    }
  };

  const toggleAi = async () => {
    setBusy(true);
    setErr('');
    try {
      const paused = !l.aiPaused;
      await setAiPaused(session, lead.leadId, paused);
      await onChange(paused ? { aiPaused: true, aiPausedReason: 'owner' } : { aiPaused: false });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not change the AI');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ borderTop: '1px solid #e3ddd2', marginTop: 12, paddingTop: 12, display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <span className="meta">
          {l.aiPaused
            ? `AI paused${l.aiPausedReason === 'handoff' ? ' (it handed this lead to you)' : ''}. You're handling this lead.`
            : 'AI is answering this lead.'}
        </span>
        <button type="button" className="ghost" onClick={() => void toggleAi()} disabled={busy}>
          {l.aiPaused ? 'Resume AI' : 'Pause AI'}
        </button>
      </div>
      <textarea
        rows={3}
        maxLength={1000}
        value={text}
        placeholder="Text this lead. It goes out from your business number."
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) void send(); }}
      />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
        <span className="meta">
          {n ? `${n} characters, about ${segs} ${segs === 1 ? 'text' : 'texts'}. Emoji are removed.` : 'Ctrl+Enter sends.'}
        </span>
        <button className="primary" type="button" disabled={busy || !n} onClick={() => void send()}>
          {busy ? 'Sending...' : 'Send text'}
        </button>
      </div>
      {!l.aiPaused && <span className="meta">Sending pauses the AI on this lead so you're not both talking.</span>}
      {err && <p className="err" style={{ margin: 0 }}>{err}</p>}
    </div>
  );
}

type LeadD = Lead & { details?: Record<string, { value: string; by: 'ai' | 'owner'; at: string }>; summary?: string };

const PANEL_STYLE = {
  position: 'sticky', top: 0, zIndex: 2,
  border: '1px solid #e3ddd2', borderRadius: 10, padding: '8px 10px', margin: '0 0 10px',
  background: '#fffdf8', boxShadow: '0 6px 10px -8px rgba(0,0,0,0.35)',
} as const;
const LINK_STYLE = {
  background: 'none', border: 'none', padding: '2px 4px', fontSize: 12, color: '#2f4a6b',
  cursor: 'pointer', textDecoration: 'underline',
} as const;
const GRID_STYLE = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '6px 8px', marginTop: 6 } as const;
const FIELD_LABEL = { fontSize: 11, gap: 2, fontWeight: 500 } as const;
const FIELD_INPUT = { padding: '4px 8px', fontSize: 13, height: 30, minHeight: 0, boxSizing: 'border-box' } as const;
const DETAIL_TAG = { marginLeft: 5, fontSize: 9, padding: '0 4px', borderRadius: 999, background: '#ece7dc', color: '#6b6355', fontWeight: 500 } as const;

function DetailsPanel({ session, lead, fields, onSaved }: {
  session: Session;
  lead: Lead;
  fields: LeadField[];
  onSaved: (fresh: Lead) => void;
}) {
  const l = lead as LeadD;
  const [draft, setDraft] = useState<Record<string, string> | null>(null);
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { setDraft(null); setErr(''); }, [lead.leadId]);
  if (!fields.length) return null;

  const current = (k: string) => l.details?.[k]?.value ?? '';
  const have = fields.filter((f) => current(f.key)).length;
  const startDraft = () => Object.fromEntries(fields.map((f) => [f.key, current(f.key)]));
  const edit = (k: string, v: string) => setDraft({ ...(draft ?? startDraft()), [k]: v });

  // Nothing collected yet: stay out of the way. A small link lets you type details by hand.
  if (have === 0 && !draft) {
    return (
      <button type="button" style={LINK_STYLE} onClick={() => { setOpen(true); setDraft(startDraft()); }}>
        + Add lead details
      </button>
    );
  }

  const save = async () => {
    if (!draft) return;
    const changed: Record<string, string> = {};
    for (const f of fields) {
      const after = (draft[f.key] ?? '').trim();
      if (after !== current(f.key)) changed[f.key] = after;
    }
    if (!Object.keys(changed).length) { setDraft(null); return; }
    setBusy(true);
    setErr('');
    try {
      const r = await saveLeadDetails(session, lead.leadId, changed);
      setDraft(null);
      onSaved(r.lead);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={PANEL_STYLE}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Lead details: {have} of {fields.length}</span>
        <button type="button" style={LINK_STYLE} onClick={() => setOpen(!open)}>{open ? 'Hide' : 'Show'}</button>
      </div>
      {l.summary && (
        <div
          style={{
            fontSize: 12, color: '#6b6355', marginTop: 2,
            ...(open ? {} : { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
          }}
        >
          {l.summary}
        </div>
      )}
      {open && (
        <>
          <div style={GRID_STYLE}>
            {fields.map((f) => {
              const d = l.details?.[f.key];
              return (
                <label key={f.key} style={FIELD_LABEL}>
                  <span>
                    {f.label}{f.required ? ' *' : ''}
                    {d?.value && !draft && <span style={DETAIL_TAG}>{d.by === 'owner' ? 'You' : 'AI'}</span>}
                  </span>
                  <input
                    style={FIELD_INPUT}
                    value={draft ? draft[f.key] ?? '' : current(f.key)}
                    placeholder="-"
                    onChange={(e) => edit(f.key, e.target.value)}
                  />
                </label>
              );
            })}
          </div>
          {draft && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
              <button className="primary" type="button" style={{ padding: '4px 12px', fontSize: 13 }} disabled={busy} onClick={() => void save()}>
                {busy ? 'Saving...' : 'Save'}
              </button>
              <button className="ghost" type="button" style={{ padding: '4px 12px', fontSize: 13 }} disabled={busy} onClick={() => setDraft(null)}>
                Cancel
              </button>
              <span className="meta" style={{ fontSize: 11 }}>* Required for Qualified. Your edits are never overwritten by the AI.</span>
            </div>
          )}
          {err && <p className="err" style={{ margin: '6px 0 0', fontSize: 12 }}>{err}</p>}
        </>
      )}
    </div>
  );
}

export function LeadsTab({ session }: { session: Session }) {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [todayOnly, setTodayOnly] = useState(true);
  const [sel, setSel] = useState<Lead | null>(null);
  const [turns, setTurns] = useState<ConversationTurn[]>([]);
  const [err, setErr] = useState('');

  async function refresh() {
    try {
      setLeads(await listLeads(session));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not load leads');
    }
  }

  useEffect(() => { void refresh(); }, [session]);
  const [fields, setFields] = useState<LeadField[]>([]);
  useEffect(() => { void getLeadFields(session).then(setFields).catch(() => setFields([])); }, [session]);

  useEffect(() => {
    if (!sel) return;
    void getConversation(session, sel.leadId).then(setTurns).catch(() => setTurns([]));
  }, [session, sel]);

  // Keep the open thread fresh so lead replies show up without clicking.
  const selId = sel?.leadId;
  useEffect(() => {
    if (!selId) return;
    const t = setInterval(() => {
      void getConversation(session, selId).then(setTurns).catch(() => undefined);
      void getLeadById(session, selId).then((fresh) => {
        setSel((cur) => (cur && cur.leadId === fresh.leadId ? { ...cur, ...fresh } : cur));
        setLeads((prev) => prev.map((x) => (x.leadId === fresh.leadId ? { ...x, ...fresh } : x)));
      }).catch(() => undefined);
    }, 10000);
    return () => clearInterval(t);
  }, [session, selId]);

  // Jump to the newest text when a lead opens or a new text arrives. The 10s refresh alone doesn't move it.
  const threadRef = useRef<HTMLDivElement>(null);
  const lastKey = useRef('');
  useEffect(() => {
    const key = `${turns[0]?.leadId ?? ''}:${turns.length}`;
    if (key === lastKey.current) return;
    lastKey.current = key;
    const el = threadRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      if (el.scrollHeight > el.clientHeight + 4) el.scrollTop = el.scrollHeight;
      else el.lastElementChild?.scrollIntoView({ block: 'nearest' });
    });
  }, [turns]);

  const shown = useMemo(
    () => leads.filter((l) => (todayOnly ? isToday(l.createdAt) || isToday(l.updatedAt) : true)),
    [leads, todayOnly],
  );

  const missedToday = leads.filter((l) => l.source === 'missed-call' && isToday(l.createdAt)).length;
  const bookedToday = leads.filter((l) => l.status === 'booked' && isToday(l.updatedAt)).length;
  const closedRev = leads.filter((l) => l.status === 'closed-won').reduce((s, l) => s + (l.revenue ?? 0), 0);

  return (
    <div className="leads-page">
      <div className="topbar">
        <div>
          <h1>Leads</h1>
          <div className="sub">Today’s board. Click a row for the text thread.</div>
        </div>
      </div>
      <div className="stats">
        <div className="stat"><b>{missedToday}</b><span>Missed calls today</span></div>
        <div className="stat"><b>{bookedToday}</b><span>Estimates booked today</span></div>
        <div className="stat"><b>{money(closedRev)}</b><span>Closed project revenue</span></div>
      </div>
      <div className="toolbar">
        <button className={`toggle ${todayOnly ? 'on' : ''}`} onClick={() => setTodayOnly(true)}>Today</button>
        <button className={`toggle ${!todayOnly ? 'on' : ''}`} onClick={() => setTodayOnly(false)}>All leads</button>
        <button className="ghost" onClick={() => void refresh()}>Refresh</button>
        {err && <span className="err">{err}</span>}
      </div>
      <div className="layout">
        <div className="card">
          {shown.length === 0 && <div className="empty">No leads in this view yet. Call or text 248-686-3933, then refresh.</div>}
          {shown.map((l) => (
            <div
              key={l.leadId}
              className={`row ${sel?.leadId === l.leadId ? 'sel' : ''}`}
              onClick={() => setSel(l)}
            >
              <div>
                <div className="name">{l.name || l.phone || l.leadId}{optedOut(l) && <span style={OPTOUT_TAG}>Opted out</span>}{(l as LeadX).aiPaused && !optedOut(l) && <span style={PAUSED_TAG}>AI paused</span>}</div>
                <div className="meta">{when(l.updatedAt || l.createdAt)}</div>
              </div>
              <span className={`pill ${sourceClass(l.source)}`}>{sourceLabel(l.source)}</span>
              <select
                value={l.status}
                onClick={(e) => e.stopPropagation()}
                onChange={async (e) => {
                  const status = e.target.value as LeadStatus;
                  try {
                    await patchLead(session, l.leadId, { status });
                    setLeads((prev) => prev.map((x) => (x.leadId === l.leadId ? { ...x, status } : x)));
                  } catch (ex) {
                    setErr(ex instanceof Error ? ex.message : 'Update failed');
                  }
                }}
              >
                {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
              </select>
              <div className="money">{money(l.revenue)}</div>
            </div>
          ))}
        </div>
        <div className="card thread" ref={threadRef}>
          {!sel && <div className="empty">Select a lead to read the SMS thread and what the agent collected.</div>}
          {sel && (
            <>
              <h2>{sel.name || sel.phone} · {sourceLabel(sel.source)}</h2>
              <div className="meta" style={{ marginBottom: 12 }}>{sel.leadId}</div>
              <DetailsPanel
                session={session}
                lead={sel}
                fields={fields}
                onSaved={(fresh) => {
                  setSel((cur) => (cur && cur.leadId === fresh.leadId ? { ...cur, ...fresh } : cur));
                  setLeads((prev) => prev.map((x) => (x.leadId === fresh.leadId ? { ...x, ...fresh } : x)));
                }}
              />
              {optedOut(sel) && (
                <p className="err" style={{ marginTop: 0 }}>
                  Opted out of texts{(sel as Lead & OptOut).smsOptOutAt ? ` on ${when((sel as Lead & OptOut).smsOptOutAt!)}` : ''}. Nobody can text this number until they text START.
                </p>
              )}
              {turns.length === 0 && <div className="empty">No messages stored for this lead yet.</div>}
              {turns.map((t) => (
                <div key={`${t.timestamp}-${t.seq}`} className={`bubble ${t.role === 'lead' ? 'lead' : t.role === 'owner' ? 'owner' : 'assistant'}`}>
                  {t.text}
                  <span className="t">{t.role === 'owner' ? 'You, ' : t.role === 'assistant' ? 'AI, ' : ''}{when(t.timestamp)}</span>
                </div>
              ))}
              <Composer
                session={session}
                lead={sel}
                onChange={async (patch) => {
                  const id = sel.leadId;
                  setSel((cur) => (cur && cur.leadId === id ? ({ ...cur, ...patch } as Lead) : cur));
                  setLeads((prev) => prev.map((x) => (x.leadId === id ? ({ ...x, ...patch } as Lead) : x)));
                  setTurns(await getConversation(session, id));
                }}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
